/**
 * setValue → BEHAVIOR_CHANGED bridge.
 *
 * Runtime-tier writes (`Properties.setValue` from the AutoUI swatch, graph taps,
 * play mode) do NOT go through the editor commit path, so they never dispatch a
 * `BEHAVIOR_CHANGED` event — which is what restales state/keyframe thumbnails and
 * refreshes the pose strip. That left an asymmetry: editing a color from the
 * behaviors panel recaptured the affected state cards, but picking the same color
 * from the AutoUI variant swatch did not.
 *
 * This bridge subscribes to every Properties behavior's `onValueChangedObservable`
 * and dispatches a Properties `BEHAVIOR_CHANGED` for the owning object's path, so
 * the existing thumbnail/pose-strip listeners recapture exactly as they do for a
 * commit. Dispatches are coalesced per object across a microtask (a color drag can
 * stream many writes; one recapture per object per tick is enough).
 *
 * Installed once per scene load; re-syncs on topology changes to pick up objects
 * created after load. Idempotent per Properties instance (WeakSet-guarded).
 */

import type { TraceScene } from 'trace-viewer/scene';
import type { SceneObject } from 'trace-viewer/scene';
import { Properties } from 'trace-viewer/behaviors';
import { EDITOR_EVENTS, dispatchBehaviorChanged } from './editor-events';

/** Active bridge disposer, so a new scene load tears down the previous one. */
let disposeActive: (() => void) | null = null;

export function installSetValueThumbnailBridge(traceScene: TraceScene): void {
    disposeActive?.();

    const subscribed = new WeakSet<Properties>();
    const disposers: Array<() => void> = [];
    const pending = new Set<SceneObject>();
    let flushQueued = false;

    const flush = (): void => {
        flushQueued = false;
        for (const obj of pending) {
            dispatchBehaviorChanged({
                objectPath: obj.getPath(),
                behaviorType: 'Properties',
                action: 'updated',
            });
        }
        pending.clear();
    };

    const queue = (obj: SceneObject): void => {
        pending.add(obj);
        if (flushQueued) return;
        flushQueued = true;
        queueMicrotask(flush);
    };

    const subscribeObject = (obj: SceneObject): void => {
        const props = obj.getBehavior(Properties);
        if (!props || subscribed.has(props)) return;
        subscribed.add(props);
        const observer = props.onValueChangedObservable.add(() => queue(obj));
        disposers.push(() => props.onValueChangedObservable.remove(observer));
    };

    const syncAll = (): void => {
        for (const obj of traceScene.getSceneObjects()) subscribeObject(obj);
    };
    syncAll();

    // Resync when the scene changes. Two triggers, because they cover disjoint
    // cases and both matter for the variant workflow:
    //  - topology change: objects created / reparented / deleted;
    //  - BEHAVIOR_CHANGED: a Properties behavior ADDED to an existing object
    //    (e.g. createMaterialVariantSet binding an object that had none) — this
    //    fires no topology event, so a topology-only resync would miss it.
    // Both are idempotent (WeakSet guard) and only subscribe, so re-entrancy from
    // the bridge's own dispatch is harmless.
    const disposeTopology = traceScene.onTopologyChanged(syncAll);
    const onBehaviorChanged = (): void => syncAll();
    window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);

    disposeActive = () => {
        disposeTopology();
        window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);
        for (const d of disposers) d();
        disposers.length = 0;
        pending.clear();
        disposeActive = null;
    };
}
